import classNames from 'classnames';
import { BlockEmbed } from 'quill/blots/block';
import Quill from 'quill/core';
import type { HTMLAttributes } from 'react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as styles from './PostLayout.module.scss';
import { usePostLayoutState } from './PostLayoutProvider';
import {
  INNER_RIGHT_CONTENT_PADDING,
  MAGNETIC_WALL_WIDE_WIDTH,
} from './PostLayoutSizes';
import useBannerOverlap from './useBannerOverlap';
import PageLayout from '../app/layout/PageLayout';
import Slot from '../components/Slot';
import { useQuill } from '../editor/QuillContext';
import { usePostLayoutSize } from '../utils/responsive/PostLayoutSizeProvider';

type SetElement = (el: HTMLElement | null) => void;

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const SetElementsContext = React.createContext<{
  setMagneticWallEl: SetElement | undefined;
  setTocEl: SetElement | undefined;
  setEditorEl: SetElement | undefined;
}>({
  setMagneticWallEl: undefined,
  setTocEl: undefined,
  setEditorEl: undefined,
});

const CanShowWideMagneticWallItemsContext = React.createContext(false);
export const useCanShowWideMagneticWallItems = () =>
  useContext(CanShowWideMagneticWallItemsContext);

interface WideMagneticWallItemsProviderProps {
  children: React.ReactNode;
  containerRef: React.RefObject<HTMLElement>;
}

const WideMagneticWallItemsProvider = ({
  children,
  containerRef,
}: WideMagneticWallItemsProviderProps) => {
  const [canShowWideMagneticWallItems, setCanShowWideMagneticWallItems] =
    useState(false);

  const [magneticWallEl, setMagneticWallEl] = useState<HTMLElement | null>(
    null,
  );
  const [tocEl, setTocEl] = useState<HTMLElement | null>(null);
  const [editorEl, setEditorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const containerEl = containerRef?.current;

    if (!containerEl || !magneticWallEl || !editorEl || !tocEl) {
      return undefined;
    }

    const recalculate = () => {
      const editorRect = editorEl.getBoundingClientRect();
      const magneticWallRect = magneticWallEl.getBoundingClientRect();

      const spaceNeeded =
        MAGNETIC_WALL_WIDE_WIDTH -
        magneticWallRect.width +
        INNER_RIGHT_CONTENT_PADDING;
      const spaceAvailable = magneticWallRect.left - editorRect.right;

      setCanShowWideMagneticWallItems(spaceAvailable >= spaceNeeded);
    };

    const observer = new ResizeObserver(recalculate);

    observer.observe(containerEl);
    observer.observe(magneticWallEl);
    observer.observe(tocEl);

    return () => {
      observer.disconnect();
    };
  }, [containerRef, magneticWallEl, tocEl, editorEl]);

  const setElementsContextValue = useMemo(
    () => ({
      setMagneticWallEl,
      setTocEl,
      setEditorEl,
    }),
    [],
  );

  return (
    <SetElementsContext.Provider value={setElementsContextValue}>
      <CanShowWideMagneticWallItemsContext.Provider
        value={canShowWideMagneticWallItems}
      >
        {children}
      </CanShowWideMagneticWallItemsContext.Provider>
    </SetElementsContext.Provider>
  );
};

interface RootProps extends Omit<HTMLAttributes<HTMLElement>, 'className'> {
  children?: React.ReactNode;
  hasBanner?: boolean;
  topbar?: React.ReactNode;
}

const Root = React.forwardRef<HTMLElement, RootProps>(
  ({ children, hasBanner, topbar, ...rest }, ref) => {
    const rootRef = useRef<HTMLElement | null>(null);
    const { isTOCExpanded, isMagneticWallExpanded } = usePostLayoutState();
    const postLayoutSize = usePostLayoutSize();

    return (
      <Slot ref={ref}>
        <article
          ref={rootRef}
          {...rest}
          className={classNames(styles.postLayoutRoot, {
            [styles.hasBanner]: hasBanner,
            [styles.tablet]: postLayoutSize === 'tablet',
            [styles.mobile]: postLayoutSize === 'mobile',
            [styles.monitor]: postLayoutSize === 'monitor',
            [styles.isTOCExpanded]: isTOCExpanded,
            [styles.isMagneticWallExpanded]: isMagneticWallExpanded,
          })}
        >
          <WideMagneticWallItemsProvider containerRef={rootRef}>
            {topbar}
            <PageLayout.Content
              noPaddingX
              noPaddingY
              className={styles.postLayoutContainer}
            >
              {children}
            </PageLayout.Content>
          </WideMagneticWallItemsProvider>
        </article>
      </Slot>
    );
  },
);

const StateBar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...rest }, ref) => (
  <div
    ref={ref}
    className={classNames(styles.postLayoutStateBar, className)}
    {...rest}
  >
    <div className={styles.postLayoutStateBarInner}>{children}</div>
  </div>
));

interface AboveProps {
  children?: React.ReactNode;
}

const Above = React.forwardRef<HTMLElement, AboveProps>(({ children }, ref) => (
  <header ref={ref} className={styles.postLayoutAbove}>
    <div className={styles.postLayoutAboveInner}>{children}</div>
  </header>
));

interface ControlBarProps {
  children: React.ReactNode;
}

const ControlBar = ({ children }: ControlBarProps) => (
  <div className={styles.postLayoutControlBar}>{children}</div>
);

interface EditorProps {
  children: React.ReactNode;
}

const Editor = React.forwardRef<HTMLElement, EditorProps>(
  ({ children }, ref) => {
    const { setEditorEl } = useContext(SetElementsContext);

    return (
      <Slot ref={ref}>
        <article
          ref={setEditorEl}
          className={styles.postLayoutEditor}
          aria-label="Post content"
        >
          {children}
        </article>
      </Slot>
    );
  },
);

const MagneticWall = React.forwardRef<HTMLElement, HTMLAttributes<HTMLElement>>(
  ({ children, ...rest }, ref) => {
    const { setMagneticWallEl } = useContext(SetElementsContext);

    return (
      <Slot ref={ref}>
        <aside
          ref={setMagneticWallEl}
          className={styles.postLayoutMagneticWall}
          {...rest}
        >
          {children}
        </aside>
      </Slot>
    );
  },
);

interface BelowProps {
  children: React.ReactNode;
}

const Below = ({ children }: BelowProps) => {
  const footerRef = useRef<HTMLElement>(null);
  const { quill, readOnly } = useQuill();
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <footer
      className={classNames(styles.postLayoutBelow, {
        [styles.readOnly]: readOnly,
      })}
      onClick={() => {
        if (!footerRef.current || !quill || readOnly) return;

        if (!footerRef.current.hasChildNodes()) {
          const [lastLine] = quill.getLine(quill.getLength() - 1);
          const isLastLineEmbed = lastLine instanceof BlockEmbed;

          if (isLastLineEmbed) {
            quill.insertText(quill.getLength(), '\n', Quill.sources.USER);
          }

          quill.setSelection(
            { index: quill.getLength() - 1, length: 0 },
            Quill.sources.USER,
          );
        }
      }}
      ref={footerRef}
    >
      {children}
    </footer>
  );
};

interface BannerProps {
  children: React.ReactNode;
  className?: string;
}

const Banner = ({ children, className }: BannerProps) => {
  const bannerOverlap = useBannerOverlap();

  return (
    <div
      className={classNames(styles.postLayoutBanner, className)}
      style={{ marginBottom: -bannerOverlap }}
    >
      {children}
    </div>
  );
};

interface ControlsProps {
  children: React.ReactNode;
  className: string;
}

const Controls = ({ children, className }: ControlsProps) => (
  <div className={classNames(styles.postLayoutBannerControls, className)}>
    {children}
  </div>
);

const TOC = ({ children, className, ...rest }: HTMLAttributes<HTMLElement>) => {
  const { setTocEl } = useContext(SetElementsContext);

  return (
    <aside
      ref={setTocEl}
      className={classNames(className, styles.postLayoutTOC)}
      {...rest}
    >
      {children}
    </aside>
  );
};

const Gutter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...rest }, ref) => (
  <div
    ref={ref}
    className={classNames(className, styles.postLayoutGutter)}
    {...rest}
  >
    {children}
  </div>
));

export default {
  Root,
  StateBar,
  Above,
  ControlBar,
  Editor,
  MagneticWall,
  Below,
  Banner,
  Controls,
  TOC,
  Gutter,
};
